import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const EntityContext = createContext();

export const useEntity = () => {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within an EntityProvider');
  }
  return context;
};

export const EntityProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentEntity, setCurrentEntity] = useState(null);
  const [availableEntities, setAvailableEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadEntities();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/entities/active');
      const entities = response.data.data?.entities || [];
      setAvailableEntities(entities);

      // Get current entity from localStorage or use default
      const savedEntityId = localStorage.getItem('currentEntityId');
      if (savedEntityId) {
        const savedEntity = entities.find(e => e.id === parseInt(savedEntityId));
        if (savedEntity) {
          setCurrentEntity(savedEntity);
          setLoading(false);
          return;
        }
      }

      // Use default entity or first available
      const defaultEntity = entities.find(e => e.is_default === 1 || e.is_default === true) || entities[0];
      if (defaultEntity) {
        setCurrentEntity(defaultEntity);
        localStorage.setItem('currentEntityId', defaultEntity.id.toString());
      }
    } catch (error) {
      console.error('Error loading entities:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeEntity = (entity) => {
    if (entity && availableEntities.some(e => e.id === entity.id)) {
      setCurrentEntity(entity);
      localStorage.setItem('currentEntityId', entity.id.toString());
      // Reload to refresh all data, but keep current path
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== '/' && currentPath !== '/dashboard') {
        // Store path with timestamp (valid for 2 seconds only)
        localStorage.setItem('entityChangeRedirect', JSON.stringify({
          path: currentPath,
          timestamp: Date.now()
        }));
      }
      window.location.reload();
    }
  };

  const value = {
    currentEntity,
    availableEntities,
    loading,
    changeEntity,
    reloadEntities: loadEntities
  };

  return (
    <EntityContext.Provider value={value}>
      {children}
    </EntityContext.Provider>
  );
};

